// Custom router search-string serializers (B3 PR4 / B3d).
//
// TanStack Router's default search encoder JSON-stringifies values
// into URL params (e.g. `?tag=%5B%22a%22%2C%22b%22%5D` for an array).
// The B3d acceptance brief calls for repeated-key encoding instead —
// `?tag=a&tag=b` — which is the natural HTTP query convention and
// what FastAPI's `tag?: string[]` query param consumes without
// extra coercion.
//
// Switching the serializers is router-wide. Only one other route
// uses search params today (`/login?redirect=...`, a single string
// value) and that round-trips identically under both schemes.
//
// Semantics applied here:
//   * arrays → repeated keys (`?tag=a&tag=b`)
//   * empty arrays / empty strings / null / undefined → omitted
//   * booleans → `'1'` when true, omitted when false (presence-truthy)
//   * numbers → `.toString()`
//
// Per-route `validateSearch` is responsible for coercing the string
// values this parser returns back into the typed shape the route
// wants (e.g. `'1'` → `true`, `'3'` → `3`).

export function parseSearch(rawSearch: string): Record<string, unknown> {
  if (!rawSearch) return {};
  const stripped = rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch;
  if (!stripped) return {};
  const params = new URLSearchParams(stripped);
  const out: Record<string, unknown> = {};
  for (const key of new Set(params.keys())) {
    const all = params.getAll(key);
    out[key] = all.length > 1 ? all : all[0];
  }
  return out;
}

export function stringifySearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  // Sort keys so the resulting URL is stable regardless of insertion
  // order. Stable URLs make TanStack Query cache keys & route preloads
  // hit consistently on back/forward nav and link-share.
  const keys = Object.keys(search).sort();
  for (const key of keys) {
    const value = search[key];
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      for (const v of value) {
        if (typeof v === 'string') {
          if (v !== '') params.append(key, v);
        } else if (typeof v === 'number') {
          if (Number.isFinite(v)) params.append(key, v.toString());
        } else if (typeof v === 'boolean') {
          params.append(key, v ? '1' : '0');
        }
        // Anything else in an array (null/undefined/object) is
        // silently skipped — this serializer only handles scalar
        // values typical of URL query params.
      }
    } else if (typeof value === 'boolean') {
      if (value) params.set(key, '1');
    } else if (typeof value === 'number') {
      if (Number.isFinite(value)) params.set(key, value.toString());
    } else if (typeof value === 'string') {
      if (value !== '') params.set(key, value);
    }
    // Non-scalar values silently dropped (objects/Date/Map/etc.).
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}
